import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./style.css";
import { AutoComplete, Title } from "../../Atoms";
import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, useDisclosure } from "@chakra-ui/react";
import { AddGeofence } from "../../../Pages";
import { getAlerts } from "../../../APIs";
import { GEOFENCE_TRIGGER_TYPE, LOC_OPTIONS, OVERSPEED_TRIGGER_TYPE } from "../../../Library/constants";
import { StoreContext } from "../../../Library/contexts";
import { PenIcon } from "../../../Assets/images/svgs";
import { useSearchParams } from "react-router-dom";

const OverspeedLocationSelector = ({ handleLocationData }) => {
  const LOCATION_OPTIONS = useMemo(() => [
    {
      key: LOC_OPTIONS.APPLY_EVERYWHERE,
      title: "Apply this overspeed everywhere",
      desc: "Trigger alerts when speed limit is crossed — no area restriction.",
      showExtra: false,
    },
    {
      key: LOC_OPTIONS.INSIDE,
      title: "Inside a geofence",
      desc: "Trigger overspeed alert only when vehicles are inside a selected area.",
      showExtra: true,
    },
    {
      key: LOC_OPTIONS.OUTSIDE,
      title: "Outside a geofence",
      desc: "Trigger overspeed alert when vehicle is outside a selected area.",
      showExtra: true,
    },
  ], []);

  const [ selectedLocType, setSelectedLocType ] = useState(LOC_OPTIONS.APPLY_EVERYWHERE);
  const [ selectedGf, setSelectedGf ] = useState(null);
  const [ emitAlertData, setEmitAlertData ] = useState(false);
  const [ searchParams, setSearchParams ] = useSearchParams();
  const [ isGeofenceEdit, setIsGeofenceEdit ] = useState(false);

  const allGeofencesRef = useRef([]);
  const selectedLocTypeRef = useRef(null);
  const fenceToEditFoundOnce = useRef(false);
  const overspeedAlertsFetchedOnce = useRef(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { allOverspeedAlerts, setAllOverspeedAlerts, allGeofences, setAllGeofences } = useContext(StoreContext);

  useEffect(() => {
    setIsGeofenceEdit(!!searchParams.get("editIdx"));
  }, [ searchParams ]);

  useEffect(() => {
    if (allOverspeedAlerts && allOverspeedAlerts.length) return;
    if (overspeedAlertsFetchedOnce.current) return;

    getAlerts(null, (err, data) => {
      overspeedAlertsFetchedOnce.current = true;

      if (err) console.error("Error fetching geofence alerts", err);
      else {
        let allGfData = data.filter(alert => alert.typ === GEOFENCE_TRIGGER_TYPE);
        setAllGeofences(allGfData);
        allGeofencesRef.current = allGfData;
        setAllOverspeedAlerts(data.filter(alert => alert.typ === OVERSPEED_TRIGGER_TYPE));
      }
    });
  }, [ allOverspeedAlerts ]);

  useEffect(() => {
    let isEdit = searchParams.get("edit");
    if (!allOverspeedAlerts || !allOverspeedAlerts.length || !allGeofences || !allGeofences.length) return;
    if (isEdit && !fenceToEditFoundOnce.current) {
      let alertId = +searchParams.get("idx");
      let alertToEdit = allOverspeedAlerts.find(alert => alert.idx === alertId);
      if (alertToEdit) {
        if (!alertToEdit.gfId) return
        if (alertToEdit.gfId) {
          let selGeofence = allGeofences.find(fence => fence.idx === alertToEdit.gfId);
          if (selGeofence) {
            fenceToEditFoundOnce.current = true;
            setSelectedGf(selGeofence);
            if (alertToEdit.out) {
              setSelectedLocType(LOC_OPTIONS.OUTSIDE);
            } else setSelectedLocType(LOC_OPTIONS.INSIDE);
          } else delete alertToEdit.gfId;
        }
      }
    }
  }, [ searchParams, allOverspeedAlerts, allGeofences ]);

  const handleGeofenceData = useCallback((gfData) => {
    setEmitAlertData(false);
    let isExistingGeofence = allGeofences.find(gf => gf.idx === gfData.idx);
    if (isExistingGeofence) {
      let updData = {};
      if (gfData.title !== isExistingGeofence.title) updData.title = gfData.title;
      if (gfData.region !== isExistingGeofence.region) updData.region = gfData.region;
      if (gfData.circle) {
        if (gfData.circle.clat !== isExistingGeofence.clat || gfData.circle.clong !== isExistingGeofence.clong) {
          updData.clat = gfData.circle.clat;
          updData.clong = gfData.circle.clong;
        }
        if (gfData.circle.r) updData.r = gfData.circle.r;
      }
      if (gfData.polygon) updData.vertices = gfData.polygon;

      let geofenceObj = { idx: isExistingGeofence.idx, ...updData, typ: GEOFENCE_TRIGGER_TYPE, selectedLocType: selectedLocTypeRef.current };
      if (gfData.idx === -1) geofenceObj.insert = true;
      else geofenceObj.update = true;

      setAllGeofences(prev => 
        prev.map(fence => fence.idx === isExistingGeofence.idx ? { ...fence, ...updData } : fence)
      );

      handleLocationData(isExistingGeofence.idx === -1 ? { ...isExistingGeofence, ...gfData, ...geofenceObj } : geofenceObj);

      setSelectedGf(isExistingGeofence.idx === -1 ? { ...isExistingGeofence, ...gfData, ...geofenceObj } : isExistingGeofence);
      onClose();
      return;
    }

    allGeofencesRef.current = allGeofences.filter(fence => fence.idx !== -1);
    let newGfData = { ...gfData, idx: -1 };
    if (gfData.polygon) newGfData.vertices = gfData.polygon;
    else if (gfData.circle) newGfData = { ...newGfData, ...newGfData.circle };

    allGeofencesRef.current = [ newGfData, ...allGeofencesRef.current ];
    setAllGeofences(allGeofencesRef.current);
    let geofenceData = { ...newGfData, typ: GEOFENCE_TRIGGER_TYPE, selectedLocType: selectedLocTypeRef.current, insert: true };
    handleLocationData(geofenceData);
    setSelectedGf(geofenceData);
    onClose();
  }, [ onClose, allGeofences ]);

  useEffect(() => {
    global.EE.addEventListener(global.EE.EVENT_TYPES.GEOFENCE_MODAL_DATA, handleGeofenceData);

    return () => {
      global.EE.removeEventListener(global.EE.EVENT_TYPES.GEOFENCE_MODAL_DATA, handleGeofenceData);
    }
  }, [ handleGeofenceData ]);

  const handleGfSelect = (gfIdx) => {
    let geofence = allGeofences.find(gf => gf.idx === +gfIdx);
    if (geofence) {
      handleLocationData({ ...geofence, typ: GEOFENCE_TRIGGER_TYPE, selectedLocType: selectedLocTypeRef.current });
      setSelectedGf(geofence);
    }
  }

  const handleGeofenceEdit = () => {
    const newParams = new URLSearchParams(searchParams);
    let isEdit = newParams.get("edit");
    newParams.set(isEdit === "true" ? "editIdx" : "idx", selectedGf.idx);
    setSearchParams(newParams, { replace: true });
    onOpen();
  }

  const handleCreateGeofence = (e) => {
    const newParams = new URLSearchParams(searchParams);
    let edit = newParams.get("edit");
    let idx = newParams.get("idx");
    if (!edit && idx) newParams.delete("idx");
    newParams.delete("editIdx");
    setSearchParams(newParams, { replace: true });
    onOpen();
  }

  return (
    <div className="overspeed-location-selector">
      <div className="overspeed-loc-card">
        <Title>Set location for this overspeed alert</Title>

        <div className="select-location-radio-group">
          {LOCATION_OPTIONS.map((opt) => (
            <label
              key={opt.key}
              className="sel-loc-label"
              onClick={() => {setSelectedLocType(opt.key); selectedLocTypeRef.current = opt.key; handleLocationData({ ...selectedGf, selectedLocType: opt.key })}}
            >
              <div className="sel-loc-btn-cont">
                <div className="cust-radio-btn">
                  {selectedLocType === opt.key && (
                    <div className="cust-radio-btn-filler"></div>
                  )}
                </div>
                <div>
                  <p className="sel-loc-option-name">{opt.title}</p>
                  <p className="sel-loc-option-desc">{opt.desc}</p>
                </div>
              </div>

              {opt.showExtra && selectedLocType === opt.key && (
                <div className="add-select-geofence-section">
                  <div className={`add-geofence-section-line ${selectedGf ? "extend-line" : ""}`}></div>
                  <div className="select-prev-geofence-box">
                    <AutoComplete
                      options={allGeofences.map(gf => ({ label: gf.title, value: gf.idx }))}
                      placeholder={"Select Existing Geofence"}
                      value={selectedGf?.idx}
                      isRequired={true}
                      dontFocus={true}
                      onChange={handleGfSelect}
                    />
                    {selectedGf ? <div className="geofence-preview-box">
                      <div>
                        <p className="gf-prev-box-title">Geofence Preview</p>
                        <p className="gf-prev-name">{selectedGf.title}</p>
                      </div>
                      <Button className="gf-prev-edit-btn" onClick={handleGeofenceEdit}>
                        <PenIcon />
                        Edit
                      </Button>
                    </div> : null}
                  </div>
                  <p className="overspeed-or-text">OR</p>
                  <Button className="create-new-gf-btn" onClick={handleCreateGeofence}>
                    <span>+</span>
                    Create New Geofence
                  </Button>
                </div>
              )}

              <input type="radio" name="sel-loc" />
            </label>
          ))}
        </div>
      </div>

      <Modal isOpen={isOpen} size={"full"} onClose={onClose}>
        <ModalOverlay />
        <ModalContent className="create-gf-modal-body">
          <ModalHeader>
            <Title>Create New Geofence</Title>
          </ModalHeader>
          <ModalCloseButton className="create-gf-modal-cls-btn" />
          <ModalBody>
            <AddGeofence fromOverspeedPage={true} emitAlertData={emitAlertData} setEmitAlertData={setEmitAlertData} />
          </ModalBody>

          <ModalFooter>
            <Button variant={"outline"} onClick={() => onClose()} className="cancel-gf-modal-btn">
              Close
            </Button>
            <Button onClick={() => setEmitAlertData(true)} className="add-gf-modal-btn">
              {isGeofenceEdit ? "Update" : "Add"} Geofence
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}

export default OverspeedLocationSelector
